import React, { useState, useMemo } from "react";
import { useNewsletters } from "../../../lib/firebase/newsletters/read";
import { Link } from "react-router-dom";
import { Alert, Spinner, Table, Pagination, Form } from "react-bootstrap";
import { Edit, CirclePlus, ArrowUp, ArrowDown } from "lucide-react";

const NewsletterListView = () => {
  const { data, isLoading, error } = useNewsletters();
  const [sortDirection, setSortDirection] = useState("desc"); // Default: newest to oldest
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Default: 5 rows per page

  // Sort newsletters by daysOfPublication
  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return [...data].sort((a, b) => {
      const aDate = new Date(a.daysOfPublication);
      const bDate = new Date(b.daysOfPublication);
      return sortDirection === "desc" ? bDate - aDate : aDate - bDate;
    });
  }, [data, sortDirection]);

  // Pagination logic
  const totalRows = sortedData.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const currentData = sortedData.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  // Toggle sort direction
  const handleSortToggle = () => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
    setCurrentPage(1); // Reset to first page on sort
  };

  // Generate pagination items
  const getPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    items.push(
      <Pagination.First
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        key="first"
      />
    );

    items.push(
      <Pagination.Prev
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        key="prev"
      />
    );

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        key="next"
      />
    );

    items.push(
      <Pagination.Last
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
        key="last"
      />
    );

    return items;
  };

  return (
    <main className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h4">Newsletters</h1>
        <Link to="/admin/newsletters/form" className="btn btn-primary">
          <CirclePlus className="me-1" size={16} />
          Add Newsletter
        </Link>
      </div>

      {isLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      )}

      {error && (
        <Alert variant="danger" className="text-center">
          {error}
        </Alert>
      )}

      {!isLoading && !error && (!data || data.length === 0) && (
        <Alert variant="info" className="text-center">
          No Newsletters Found
        </Alert>
      )}

      {!isLoading && !error && currentData && currentData.length > 0 && (
        <>
          <div className="table-responsive">
            <Table
              striped
              bordered
              hover
              className="bg-white rounded shadow-sm"
            >
              <thead>
                <tr>
                  <th className="py-2 px-3">Sr. No.</th>
                  <th className="py-2 px-3">Publication Name</th>
                  <th className="py-2 px-3">Article Title</th>
                  <th className="py-2 px-3">Volume</th>
                  <th
                    onClick={handleSortToggle}
                    style={{ cursor: "pointer" }}
                    className="py-2 px-3 d-flex align-items-center"
                  >
                    Days of Publication
                    {sortDirection === "desc" ? (
                      <ArrowDown size={16} className="ms-1" />
                    ) : (
                      <ArrowUp size={16} className="ms-1" />
                    )}
                  </th>
                  <th className="py-2 px-3">PDF</th>
                  <th className="py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={item?.id}>
                    <td className="py-2 px-3">{startIndex + index + 1}</td>
                    <td className="py-2 px-3">{item?.publicationName}</td>
                    <td className="py-2 px-3">{item?.articleTitle}</td>
                    <td className="py-2 px-3">{item?.volume}</td>
                    <td className="py-2 px-3">{item?.daysOfPublication}</td>
                    <td className="py-2 px-3">
                      {item?.pdfUrl ? (
                        <a
                          href={item.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View PDF
                        </a>
                      ) : (
                        "No PDF"
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <div className="d-flex gap-2">
                        <Link
                          to={`/admin/newsletters/form?id=${encodeURIComponent(
                            item?.id
                          )}`}
                          className="btn btn-warning btn-sm"
                          // onClick={() =>
                          //   console.log(
                          //     "Navigating to edit form for id:",
                          //     item?.id
                          //   )
                          // }
                        >
                          <Edit size={16} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
            <div className="mb-3 mb-md-0">
              <Form.Group className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">Rows per page:</Form.Label>
                <Form.Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  style={{ width: "auto" }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="d-flex flex-column flex-sm-row align-items-center gap-3">
              <span>
                Showing {startIndex + 1} to {endIndex} of {totalRows} entries
              </span>
              <Pagination className="mb-0">{getPaginationItems()}</Pagination>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default NewsletterListView;
